import { setContext } from 'apollo-link-context';
import { ApolloClient } from 'apollo-client';
import { onError } from 'apollo-link-error';
import {
  InMemoryCache,
  IntrospectionFragmentMatcher,
} from 'apollo-cache-inmemory';
import { HttpLink } from 'apollo-link-http';
import { ApolloLink } from 'apollo-link';
import fetch from 'isomorphic-unfetch';
import getConfig from 'next/config';
import introspectionQueryResultData from '@g4/g4-graphql-schema/__generated/fragments.json';

import { i18nextToDefaultResolvers } from './resolvers/index';
import {
  createLink as createPendingQueriesLink,
  typeDefs as pendingQueriesTypeDefs,
} from '../PendingQueries';

import {
  AUTH_HEADER,
  TRANSACTION_HEADER,
  SYSTEM_HEADER,
  extractTokens,
  isUnauthenticated,
  redirectToLogin,
} from './authUtils';

const { serverRuntimeConfig, publicRuntimeConfig } = getConfig();

let apolloClient = null;

// Polyfill fetch() on the server (used by apollo-client)
if (!process.browser) {
  global.fetch = fetch;
}

// Build a fragment matcher from our schema's introspection data
const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData,
});

const authErrorLink = onError(({ graphQLErrors }) => {
  if (graphQLErrors) {
    const authError = graphQLErrors.find(isUnauthenticated);

    if (authError) {
      redirectToLogin();
    }
  }
});

const authLinkFactory = serverRequestContext =>
  setContext((_, { headers }) => {
    const authentication = {};

    if (process.browser) {
      // we need to get the authentication token from the cookie if we're in the browser
      // currently we choose not to do this server-side since we nevcer want to query for
      // viewer-specific info during the SSR phase
      const tokens = extractTokens();
      tokens.authToken = tokens.authToken || publicRuntimeConfig.authToken;

      if (tokens.authToken) {
        authentication[AUTH_HEADER] = tokens.authToken;
      }

      if (tokens.transactionId) {
        authentication[TRANSACTION_HEADER] = tokens.transactionId;
      }

      if (tokens.systemKey) {
        authentication[SYSTEM_HEADER] = tokens.systemKey;
      }
    } else if (serverRequestContext) {
      const {
        req: { headers },
      } = serverRequestContext;
      // varnish will attach this for internal calls
      if (headers[SYSTEM_HEADER]) {
        authentication[SYSTEM_HEADER] = headers[SYSTEM_HEADER];
      }
    }

    // return the headers to the context so httpLink can read them
    return {
      headers: {
        ...headers,
        ...authentication,
      },
    };
  });

function create(
  initialState,
  introspectionQueryResultData,
  serverRequestContext
) {
  const localTranslationsResolvers = i18nextToDefaultResolvers(
    introspectionQueryResultData
  );
  const cache = new InMemoryCache({ fragmentMatcher }).restore(
    initialState || {}
  );
  const customFetch = (uri, options) => {
    const { operationName } = JSON.parse(options.body);
    options.headers['X-APOLLO-OPERATION-NAME'] = operationName;
    return fetch(uri, options);
  };
  const httpLink = new HttpLink({
    uri: !process.browser
      ? serverRuntimeConfig.graphqlEndpoint
      : publicRuntimeConfig.graphqlEndpoint,
    credentials: 'same-origin',
    fetch: customFetch,
  });

  // localSchema defaults
  cache.writeData({
    data: {
      pendingQueries: [],
    },
  });

  const pendingQueriesLink = createPendingQueriesLink();

  const authLink = authLinkFactory(serverRequestContext);

  return new ApolloClient({
    name: publicRuntimeConfig.appName,
    version: publicRuntimeConfig.buildVersion,
    connectToDevTools: process.browser,
    ssrMode: !process.browser, // Disables forceFetch on the server (so queries are only run once)
    link: ApolloLink.from([
      pendingQueriesLink,
      authLink,
      authErrorLink,
      httpLink,
    ]),
    cache: cache,
    typeDefs: pendingQueriesTypeDefs,
    resolvers: localTranslationsResolvers,
  });
}

export default function initApollo(
  initialState,
  introspectionQueryResultData,
  serverRequestContext
) {
  // Make sure to create a new client for every server-side request so that data
  // isn't shared between connections (which would be bad)
  if (!process.browser) {
    return create(
      initialState,
      introspectionQueryResultData,
      serverRequestContext
    );
  }

  // Reuse client on the client-side
  if (!apolloClient) {
    apolloClient = create(initialState, introspectionQueryResultData);
  }

  return apolloClient;
}
